import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import dynamic from 'next/dynamic'
import { Container, useLoader, useWindowSize } from '@moneymade/moneymade-ui'
import VisibilitySensor from 'react-visibility-sensor'
import Scrollspy from 'react-scrollspy'
import Sticky from 'react-stickynode'
import { useRouter } from 'next/router'

import useTracker from 'hooks/useTracker'
import TopActions from 'modules/discover/PlatformPage/TopActions/TopActions'

import styles from './PlatformPage.module.scss'

const PlatformInfo = dynamic(() => import('modules/discover/PlatformPage/PlatformInfo/PlatformInfo'))
const StickyHeader = dynamic(() => import('modules/discover/PlatformPage/StickyHeader/StickyHeader'))
const MemberInsights = dynamic(() => import('modules/discover/PlatformPage/MemberInsights/MemberInsights'))
const TextData = dynamic(() => import('modules/discover/PlatformPage/TextData/TextData'))
const UserReviews = dynamic(() => import('modules/discover/PlatformPage/UserReviews/UserReviews'))
const PlatformNews = dynamic(() => import('modules/discover/PlatformPage/PlatformNews/PlatformNews'))
const PlatformsSimilar = dynamic(() => import('modules/discover/PlatformPage/PlatformsSimilar/PlatformsSimilar'))
const ExploreAssets = dynamic(() => import('modules/discover/PlatformPage/ExploreAssets/ExploreAssets'))

const PlatformPage = ({
  currentInvestment,
  platformReview,
  reviews,
  news,
  assetsTags,
  industriesInfo,
  topIndustry,
  industriesAvgAll,
  assetPageSlug,
  assetSection,
  rankIndustries
}) => {
  const { loaderElement } = useLoader(true)
  const { isFallback } = useRouter()

  const { handleUseTracker } = useTracker()
  const { screenWidth } = useWindowSize()

  const [stickyHeader, setStickyHeader] = useState(false)

  // Send tracking info
  useEffect(() => {
    if (currentInvestment?.slug) {
      handleUseTracker('PlatformExpandedImpression', 'platform', currentInvestment.slug)
    }
  }, [handleUseTracker, currentInvestment?.slug])

  useEffect(() => {
    if (currentInvestment?.profile?.verificationStatus === 'Verified Paying (Premium)') {
      const moneyBars = document.querySelectorAll('.money-made-embed[data-embed-widget="money-bar"]')

      const combined = [...moneyBars]
      // eslint-disable-next-line no-restricted-syntax
      for (const element of combined) {
        element.remove()
      }
    }
  }, [currentInvestment?.profile?.verificationStatus])

  return !isFallback ? (
    <>
      {stickyHeader && <StickyHeader currentInvestment={currentInvestment} />}

      <section className={styles.PlatformPage}>
        <VisibilitySensor offset={{ top: -50 }} onChange={visibility => setStickyHeader(!visibility)}>
          <TopActions currentInvestment={currentInvestment} />
        </VisibilitySensor>
        <PlatformInfo currentInvestment={currentInvestment} assetPageSlug={assetPageSlug} />
        <MemberInsights
          currentInvestment={currentInvestment}
          platformReview={platformReview}
          industriesInfo={industriesInfo}
          rankInIndustriesNew={rankIndustries}
        />

        <Container className={`${styles.Content} ${industriesInfo ? styles.TwoColumns : ''}`}>
          <div className={styles.LeftSide}>
            <TextData
              currentInvestment={currentInvestment}
              industriesInfo={industriesInfo ? { ...industriesInfo, assetPageSlug } : {}}
              assetSection={assetSection}
              similars={currentInvestment?.new_similars}
              topIndustry={topIndustry}
              industriesAvgAll={industriesAvgAll}
              industry={currentInvestment?.industry}
            />

            <UserReviews
              currentInvestment={currentInvestment}
              reviews={reviews}
              rating={currentInvestment?.averageRating}
            />

            <div id="content" className={styles.InnerContainer}>
              <PlatformNews news={news} />

              <ExploreAssets assetsTags={assetsTags} />
            </div>
          </div>

          {screenWidth > 960 && industriesInfo && (
            <div className={styles.RightSide}>
              <Sticky enabled top={80} bottomBoundary="#content">
                <Scrollspy>
                  <div
                    style={{
                      overflow: 'hidden',
                      width: '402px'
                    }}
                  >
                    <PlatformsSimilar
                      similars={currentInvestment?.new_similars}
                      topIndustry={topIndustry}
                      industriesAvgAll={industriesAvgAll}
                      industry={currentInvestment?.industry}
                    />
                  </div>
                </Scrollspy>
              </Sticky>
            </div>
          )}
        </Container>
      </section>
    </>
  ) : (
    <div className={styles.Loading}>{loaderElement}</div>
  )
}

PlatformPage.propTypes = {
  currentInvestment: PropTypes.shape({
    name: PropTypes.string,
    new_similars: PropTypes.arrayOf(PropTypes.object),
    slug: PropTypes.string,
    industry: PropTypes.string,
    industrySlug: PropTypes.string,
    averageRating: PropTypes.number,
    profile: PropTypes.shape({
      verificationStatus: PropTypes.string
    })
  }),
  platformReview: PropTypes.shape({}),
  reviews: PropTypes.arrayOf(PropTypes.object),
  news: PropTypes.arrayOf(PropTypes.object),
  assetsTags: PropTypes.arrayOf(PropTypes.object),
  industriesInfo: PropTypes.shape({}),
  assetPageSlug: PropTypes.string,
  topIndustry: PropTypes.arrayOf(PropTypes.object),
  assetSection: PropTypes.shape({
    title: PropTypes.string,
    text: PropTypes.string,
    badge: PropTypes.array
  }),
  rankIndustries: PropTypes.shape({}),
  industriesAvgAll: PropTypes.shape({})
}

PlatformPage.defaultProps = {
  currentInvestment: {
    name: '',
    profile: {},
    new_similars: [],
    industry: '',
    slug: '',
    averageRating: 0
  },
  platformReview: {},
  assetSection: {},
  reviews: [],
  news: [],
  assetsTags: [],
  industriesInfo: {},
  assetPageSlug: '',
  industriesAvgAll: {},
  rankIndustries: {},
  topIndustry: []
}

export default PlatformPage
